// Presentation only: draft IDs and transitions remain the sole editable source.
export function previewText(value, limit = 100) {
  const text = String(value || '').replace(/\s+/g, ' ').trim();
  const chars = Array.from(text);
  return chars.slice(0, limit).join('') + (chars.length > limit ? '…' : '');
}

export function localizedText(values, locale) {
  if (values && values[locale]) return { text: values[locale], fallback: '' };
  const fallback = Object.keys(values || {}).find(key => values[key]);
  return { text: fallback ? values[fallback] : '', fallback: fallback || '' };
}

export function questionFor(node, questions) {
  const question = questions[node.id];
  // Typing a new catalog ID must invalidate the old question immediately.
  return question && question.id === node.question_id ? question : null;
}

export function flowGraph(draft, questions, locale) {
  const issues = [], byId = new Map(draft.nodes.map(n => [n.id, n]));
  const nodes = draft.nodes.map(node => {
    const question = questionFor(node, questions);
    const title = localizedText(question && question.title, locale);
    const stale = !!question && (question.revision || 0) !== node.question_revision;
    if (!question) issues.push({ node: node.id, text: 'Load the catalog question.' });
    if (stale) issues.push({ node: node.id, text: 'Catalog question changed. Refresh its revision and review the branches.' });
    if (title.fallback) issues.push({ node: node.id, text: `Question translation missing: ${locale}.` });
    const answers = (question && question.answers || []).map((answer, index) => {
      const label = localizedText(answer.title, locale);
      if (!answer.id) issues.push({ node: node.id, text: 'Save the question in the catalog to assign answer IDs.' });
      if (label.fallback) issues.push({ node: node.id, text: `Answer translation missing: ${locale}.` });
      return { id: answer.id, key: answer.id || `missing-${index}`, text: label.text, fallback: label.fallback,
        target: node.transitions[answer.id], skip: false, height: Math.max(92, 42 + Math.ceil(Array.from(previewText(label.text)).length / 24) * 18) };
    });
    // Keep stale mappings visible as a problem, never silently remove or reassign them.
    const answerIds = new Set(answers.map(a => a.id));
    if (question && Object.keys(node.transitions).some(id => !answerIds.has(id))) {
      issues.push({ node: node.id, text: 'A linked answer was removed from the catalog. Refresh and review.' });
    }
    answers.push({ id: null, key: 'skip', text: 'Skip this question', fallback: '', target: node.on_skip, skip: true, height: 80 });
    const headerHeight = Math.max(116, 54 + Math.ceil(Array.from(previewText(title.text)).length / 20) * 20 + (title.fallback ? 15 : 0));
    return { id: node.id, text: title.text || 'Question not loaded', fallback: title.fallback, stale,
      answers, headerHeight, width: 320, height: headerHeight + answers.reduce((sum, a) => sum + a.height, 0), x: 0, y: 0, level: 0 };
  });
  const edges = [];
  for (const node of nodes) for (const answer of node.answers) {
    if (!answer.target || (answer.target !== 'finish' && !byId.has(answer.target))) {
      issues.push({ node: node.id, text: `Choose a valid destination for “${previewText(answer.text, 40)}”.` });
    } else if (answer.target !== 'finish') edges.push({ from: node.id, to: answer.target, answerId: answer.id, key: `${node.id}:${answer.key}`, skip: answer.skip });
  }
  const reachable = new Set();
  function visit(id) { if (reachable.has(id) || !byId.has(id)) return; reachable.add(id); edges.filter(e => e.from === id).forEach(e => visit(e.to)); }
  visit(draft.entry_node_id);
  if (!byId.has(draft.entry_node_id)) issues.push({ node: '', text: 'Choose the starting question.' });
  for (const node of nodes) if (!reachable.has(node.id)) issues.push({ node: node.id, text: 'This question cannot be reached from the start.' });
  const incoming = new Map(nodes.map(n => [n.id, 0]));
  edges.forEach(e => incoming.set(e.to, incoming.get(e.to) + 1));
  const queue = nodes.filter(n => incoming.get(n.id) === 0), placed = new Set(), views = new Map(nodes.map(n => [n.id, n]));
  while (queue.length) {
    const node = queue.shift(); placed.add(node.id);
    for (const edge of edges.filter(e => e.from === node.id)) {
      const next = views.get(edge.to); next.level = Math.max(next.level, node.level + 1);
      incoming.set(next.id, incoming.get(next.id) - 1);
      if (incoming.get(next.id) === 0) queue.push(next);
    }
  }
  if (placed.size !== nodes.length) issues.push({ node: '', text: 'A branch loops back. Remove the cycle before publishing.' });
  // Invalid graphs stay editable: bounded columns instead of an infinite layout loop.
  nodes.filter(n => !placed.has(n.id)).forEach(n => { n.level = 0; });
  const topInset = 52 + nodes.length * 12;
  const columnY = {};
  for (const node of nodes) {
    node.x = 32 + node.level * 420; node.y = columnY[node.level] || topInset;
    columnY[node.level] = node.y + node.height + 40;
  }
  for (const edge of edges) {
    const from = views.get(edge.from), to = views.get(edge.to);
    const index = from.answers.findIndex(a => a.id === edge.answerId);
    const x = from.x + from.width, y = from.y + from.headerHeight + from.answers.slice(0, index).reduce((sum, a) => sum + a.height, 0) + from.answers[index].height / 2;
    const endY = to.y + 58;
    if (to.level > from.level + 1) {
      // Long branches run above intervening cards, never through hidden question text.
      const lane = 20 + nodes.indexOf(from) * 12;
      edge.path = `M ${x} ${y} L ${x + 28} ${y} L ${x + 28} ${lane} L ${to.x - 28} ${lane} L ${to.x - 28} ${endY} L ${to.x} ${endY}`;
    } else edge.path = `M ${x} ${y} C ${x + 55} ${y}, ${to.x - 55} ${endY}, ${to.x} ${endY}`;
  }
  return { nodes, edges, issues, width: Math.max(720, ...nodes.map(n => n.x + n.width + 60)),
    height: Math.max(480, ...nodes.map(n => n.y + n.height + 32)) };
}

// A new edge source → target is safe iff target cannot already reach source.
// Include every persisted edge (including skip and stale answer IDs), not only visible cards.
export function wouldCreateCycle(draft, source, target) {
  if (target === 'finish') return false;
  const byId = new Map(draft.nodes.map(n => [n.id, n])), seen = new Set(), pending = [target];
  while (pending.length) {
    const id = pending.pop();
    if (id === source) return true;
    if (seen.has(id) || !byId.has(id)) continue;
    seen.add(id);
    const node = byId.get(id);
    pending.push(...Object.values(node.transitions || {}), node.on_skip);
  }
  return false;
}
